import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const DESTINATIONS = [
  {
    to: '/universe',
    label: 'THE UNIVERSE',
    desc: 'Explore Toon City and its districts.',
    img: '/assets/arctoons-banner.webp',
    fit: 'object-cover',
    span: 'md:col-span-2',
    testid: 'dest-universe'
  },
  {
    to: '/toons',
    label: 'MEET THE TOONS',
    desc: 'Five characters. Five stories.',
    img: '/assets/karakter-3-opt.webp',
    fit: 'object-contain p-6',
    span: '',
    testid: 'dest-toons'
  },
  {
    to: '/story',
    label: 'THE STORY',
    desc: 'Five chapters of the Toonverse.',
    img: '/assets/arctoons-assets-opt.webp',
    fit: 'object-cover',
    span: '',
    testid: 'dest-story'
  },
  {
    to: '/ecosystem',
    label: 'ECOSYSTEM',
    desc: 'Build • Trade • Create • Community.',
    img: null,
    span: '',
    testid: 'dest-ecosystem'
  },
  {
    to: '/market',
    label: 'MARKET',
    desc: 'CA • BUY • CHART.',
    img: null,
    gold: true,
    span: '',
    testid: 'dest-market'
  },
  {
    to: '/community',
    label: 'COMMUNITY',
    desc: 'Join the journey on X.',
    img: '/assets/arctoons-web-background-opt.webp',
    fit: 'object-cover',
    span: 'md:col-span-2',
    testid: 'dest-community'
  }
];

export default function DestinationGrid() {
  return (
    <section
      data-testid="destination-grid"
      className="relative mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-28"
      aria-label="Explore the ARC TOONS website"
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mb-12"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">NAVIGATE</p>
        <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
          CHOOSE YOUR{' '}
          <span className="bg-gradient-to-r from-arc to-electric bg-clip-text text-transparent">PATH</span>
        </h2>
      </motion.div>

      <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
        {DESTINATIONS.map((d, i) => (
          <motion.div
            key={d.to}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className={d.span}
          >
            <Link
              to={d.to}
              data-testid={d.testid}
              className={`group relative flex h-56 flex-col justify-end overflow-hidden rounded-3xl border p-6 transition-all duration-300 hover:-translate-y-1.5 sm:h-64 ${
                d.gold ? 'border-gold/30' : 'border-electric/20'
              }`}
              style={{
                background: d.gold
                  ? 'radial-gradient(80% 100% at 50% 100%, rgba(255,181,46,0.22) 0%, rgba(8,21,43,0.9) 70%)'
                  : 'linear-gradient(180deg, rgba(8,21,43,0.9) 0%, rgba(5,11,24,0.95) 100%)'
              }}
            >
              {d.img && (
                <img
                  src={d.img}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className={`absolute inset-0 h-full w-full opacity-45 transition-all duration-500 group-hover:scale-[1.06] group-hover:opacity-60 ${d.fit}`}
                />
              )}
              <span
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(5,11,24,0.92) 10%, rgba(5,11,24,0.35) 55%, transparent 100%)'
                }}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  boxShadow: d.gold
                    ? '0 0 40px rgba(255,181,46,0.35)'
                    : '0 0 40px rgba(0,200,255,0.3)'
                }}
              />
              <span className="relative flex items-end justify-between gap-4">
                <span>
                  <span
                    className={`block font-display text-lg font-extrabold uppercase tracking-tight sm:text-xl ${
                      d.gold ? 'text-gold' : 'text-snow'
                    }`}
                  >
                    {d.label}
                  </span>
                  <span className="mt-1 block text-xs text-snow/60">{d.desc}</span>
                </span>
                <span
                  aria-hidden="true"
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 group-hover:translate-x-1 ${
                    d.gold
                      ? 'border-gold/40 text-gold group-hover:shadow-[0_0_18px_rgba(255,181,46,0.5)]'
                      : 'border-electric/40 text-electric group-hover:shadow-[0_0_18px_rgba(0,200,255,0.5)]'
                  }`}
                >
                  <ArrowRight className="h-4 w-4" />
                </span>
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
